// Pure utility functions for the game - no React dependencies

// Piece symbol maps
export const PIECE_SYMBOLS_WHITE = {
  p: '♙', n: '♘', b: '♗', r: '♖', q: '♕', k: '♔'
};

export const PIECE_SYMBOLS_BLACK = {
  p: '♟', n: '♞', b: '♝', r: '♜', q: '♛', k: '♚'
};

export const PIECE_SYMBOLS_NEUTRAL = {
  p: '♟', n: '♞', b: '♝', r: '♜', q: '♛', k: '♚'
};

export const PIECE_NAMES = {
  p: 'Pawn', n: 'Knight', b: 'Bishop', r: 'Rook', q: 'Queen', k: 'King'
};

export const PROMOTION_PIECE_NAMES = {
  q: 'Queen', r: 'Rook', b: 'Bishop', n: 'Knight'
};

// Get piece symbols for a given team color
export const getPieceSymbols = (team) => {
  return team === 'white' ? PIECE_SYMBOLS_WHITE : PIECE_SYMBOLS_BLACK;
};

// Get vote weight based on player's role
export const getVoteWeight = (role) => {
  if (!role) return 0;
  const roleLower = role.toLowerCase();

  if (roleLower.includes('pawn')) return 1;
  if (roleLower.includes('knight')) return 3;
  if (roleLower.includes('bishop')) return 3;
  if (roleLower.includes('rook')) return 5;
  if (roleLower.includes('queen')) return 7;
  if (roleLower.includes('king')) return 0;

  return 0;
};

// Get piece type code from role string
export const getPieceTypeFromRole = (role) => {
  if (!role) return null;
  const roleLower = role.toLowerCase();
  if (roleLower.includes('king')) return 'k';
  if (roleLower.includes('queen')) return 'q';
  if (roleLower.includes('rook')) return 'r';
  if (roleLower.includes('bishop')) return 'b';
  if (roleLower.includes('knight')) return 'n';
  if (roleLower.includes('pawn')) return 'p';
  return null;
};

// Filter chat messages based on selected tab
export const filterMessagesByTab = (messages, tab) => {
  if (!messages) return [];

  switch (tab) {
    case 'chat':
      return messages.filter(msg => !msg.isSuggestion && !msg.isMove);
    case 'suggested':
      return messages.filter(msg => msg.isSuggestion);
    case 'played':
      return messages.filter(msg => msg.isMove);
    default:
      return messages;
  }
};

// Get timer warning level based on remaining time and phase
export const getTimerWarningLevel = (remainingTime, phase) => {
  if (remainingTime === null || remainingTime === undefined) return null;

  if (phase === 'voting') {
    if (remainingTime <= 5) return 'critical';
    if (remainingTime <= 10) return 'urgent';
    if (remainingTime <= 15) return 'warning';
  } else {
    if (remainingTime <= 10) return 'critical';
    if (remainingTime <= 20) return 'urgent';
    if (remainingTime <= 30) return 'warning';
  }
  return null;
};

// Check if a role string indicates a King
export const isKingRole = (role) => {
  return role?.toLowerCase().includes('king') || false;
};

// Get piece symbol for a player based on their role and team
export const getPlayerPieceSymbol = (role, team) => {
  if (!role) return '👤';
  const roleLower = role.toLowerCase();
  const symbols = team === 'white' ? PIECE_SYMBOLS_WHITE : PIECE_SYMBOLS_BLACK;

  if (roleLower.includes('king')) return symbols.k;
  if (roleLower.includes('queen')) return symbols.q;
  if (roleLower.includes('rook')) return symbols.r;
  if (roleLower.includes('bishop')) return symbols.b;
  if (roleLower.includes('knight')) return symbols.n;
  if (roleLower.includes('pawn')) return symbols.p;
  return '👤';
};

// Group suggestions by move (from-to) to deduplicate
export const groupSuggestionsByMove = (suggestions) => {
  if (!suggestions) return [];

  const grouped = {};
  suggestions.forEach(suggestion => {
    const moveKey = `${suggestion.from}-${suggestion.to}`;
    if (!grouped[moveKey]) {
      grouped[moveKey] = {
        ...suggestion,
        players: [suggestion.player],
        ids: [suggestion.id],
        count: 1
      };
    } else {
      grouped[moveKey].players.push(suggestion.player);
      grouped[moveKey].ids.push(suggestion.id);
      grouped[moveKey].count += 1;
    }
  });

  return Object.values(grouped);
};

// Calculate weighted vote counts from poll votes
export const calculateVoteCounts = (votes, teamPlayers) => {
  const voteCounts = {};
  Object.entries(votes || {}).forEach(([voterEmail, moveId]) => {
    const voter = teamPlayers?.find(p => p.email === voterEmail);
    if (voter) {
      const weight = getVoteWeight(voter.role);
      voteCounts[moveId] = (voteCounts[moveId] || 0) + weight;
    }
  });
  return voteCounts;
};

// Get team members enriched with piece symbols and status
export const getTeamMembersWithPieces = (game, playerTeam, currentUserEmail) => {
  if (!game || !playerTeam) return [];
  const team = playerTeam === 'white' ? game.whiteTeam : game.blackTeam;
  const eliminatedEmails = game.eliminatedPlayers || [];

  return team?.map(player => ({
    ...player,
    pieceSymbol: getPlayerPieceSymbol(player.role, playerTeam),
    isEliminated: eliminatedEmails.includes(player.email),
    isCurrentUser: player.email === currentUserEmail
  })) || [];
};

// Check if there are any active voters on the team
export const hasActiveVoters = (game, playerTeam) => {
  if (!game || !playerTeam) return false;
  const team = playerTeam === 'white' ? game.whiteTeam : game.blackTeam;
  const eliminatedEmails = game.eliminatedPlayers || [];

  const activeVoters = team?.filter(p => {
    if (eliminatedEmails.includes(p.email)) return false;
    return !isKingRole(p.role);
  }) || [];

  return activeVoters.length > 0;
};

// Get the game end reason string
export const getGameEndReason = (chess, teamName) => {
  if (chess.isCheckmate()) {
    return `Checkmate! ${teamName} wins!`;
  } else if (chess.isStalemate()) {
    return 'Stalemate!';
  } else if (chess.isDraw()) {
    return 'Draw!';
  }
  return '';
};
